import React, { useEffect, useState } from 'react';
import 'bootstrap-select';
import { useParams, Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import LoadingSpinner from 'component/LoadingSpinner';
import ProjectService from 'service/ProjectService';
import { ProjectTaskListResp } from 'domain/payload/response/project/ProjectTaskListResp';
import { ProjectDetailResp } from 'domain/payload/response/project/ProjectDetailResp';
import TabNavigation from 'component/TabNavigator';
import { ProjectJobListResp } from 'domain/payload/response/project/ProjectJobListResp';
import ProjectJobDetailModal from './ProjectJobDetailModal';
import CustomDropdown from 'component/CustomDropdown';
import { PageSizeOption } from 'domain/constant/PageSizeOption';

const ProjectJobDetail: React.FC = () => {
  const { id } = useParams();
  const { profile } = useSelector((state: any) => state.auth);
  const projectService = ProjectService();

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);

  const [project, setProject] = useState<ProjectDetailResp | null>(null);
  const [tasks, setTasks] = useState<ProjectTaskListResp[]>([]);
  const [jobs, setJobs] = useState<ProjectJobListResp[]>([]);

  const [tabMode, setTabMode] = useState<string>('job');

  const [showModal, setShowModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState<any | null>(null);

  // LOADING
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingTask, setIsLoadingTask] = useState(false);

  useEffect(() => {
    fetchProjectDetail();
  }, []);

  useEffect(() => {
    if (tabMode === 'task') {
      fetchDataTaskList(1, pageSize);
    } else if (tabMode === 'job') {
      fetchDataJobList(1, pageSize);
    }
  }, [currentPage, pageSize]);

  useEffect(() => {
    setCurrentPage(1);
    if (tabMode === 'task') {
      fetchDataTaskList(1, pageSize);
    } else if (tabMode === 'job') {
      fetchDataJobList(1, pageSize);
    }
  }, [tabMode]);

  const fetchProjectDetail = async () => {
    const token = profile?.data.token;
    if (!token || !id) {
      if (!token) window.alert('Token tidak ditemukan. Silakan login kembali.');
      return;
    }

    setIsLoading(true);
    try {
      const res = await projectService.projectDetail(token, id);
      setProject(res.data!);
    } catch (err: any) {
      console.error(err);
      const errorMessage = err.response?.data?.message || err.message || 'Terjadi kesalahan saat mengambil detail project';
      window.alert(`Error: ${errorMessage}`);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchDataTaskList = async (page: number, size: number) => {
    const token = profile?.data.token;
    if (!token) {
      window.alert('Token tidak ditemukan. Silakan login kembali.');
      return;
    }

    setIsLoadingTask(true);
    try {
      const resTask = await projectService.projectTaskList(token, id!, null, null, page, size);
      setTasks(resTask.data || []);
      setTotalPages(resTask.pagination?.totalPage ?? 0);
      setTotalRecords(resTask.pagination?.totalRecords ?? 0);
    } catch (err: any) {
      console.error(err);
      const errorMessage = err.response?.data?.message || err.message || 'Terjadi kesalahan saat mengambil data task';
      window.alert(`Error: ${errorMessage}`);
    } finally {
      setIsLoadingTask(false);
    }
  };

  const fetchDataJobList = async (page: number, size: number) => {
    const token = profile?.data.token;
    if (!token) {
      window.alert('Token tidak ditemukan. Silakan login kembali.');
      return;
    }

    setIsLoadingTask(true);
    try {
      const resTask = await projectService.projectJobList(token, id!, "", null, page, size);
      setJobs(resTask.data || []);
      setTotalPages(resTask.pagination?.totalPage ?? 0);
      setTotalRecords(resTask.pagination?.totalRecords ?? 0);
    } catch (err: any) {
      console.error(err);
      const errorMessage = err.response?.data?.message || err.message || 'Terjadi kesalahan saat mengambil data job';
      window.alert(`Error: ${errorMessage}`);
    } finally {
      setIsLoadingTask(false);
    }
  };

  return (
    <div className="pt-1 h-100" style={{ background: '#f2f4f8' }}>
      <div className="m-4">
        <h4 className="mb-4">Job Detail</h4>
        <div className="p-4 bg-white mb-4 rounded">
          {isLoading ? (
            <LoadingSpinner />
          ) : (
            <>
              <div className="d-flex justify-content-between align-items-start mb-5">
                <div>
                  <h5 className="mb-1">{project?.name}</h5>
                  <p className="mb-1 text-muted">
                    {project?.startDate} – {project?.endDate}
                  </p>
                  <p className="mb-0 fw-semibold">{project?.instruction?.name}</p>
                </div>
                <div>
                  <Link to={`/project/${id}`}>
                    <button className="btn btn-outline-secondary">
                      Edit Project <i className="fa-regular fa-pen-to-square ms-1" />
                    </button>
                  </Link>
                </div>
              </div>

              <div>
                <div className="d-flex mb-3">
                  <TabNavigation
                    activeTab={tabMode}
                    onTabChange={(tab) => {
                      setTabMode(tab);
                      setCurrentPage(1);
                    }}
                    tabs={[
                      { key: "job", label: "Job" },
                      { key: "task", label: "Task" }
                    ]}
                  />
                </div>
                {tabMode === "job" &&
                  <>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                    </div>

                    <table className="table table-condensed table-hover">
                      <thead className="table-primary">
                        <tr>
                          <th>Name</th>
                          <th>Description</th>
                          <th>Fee</th>
                          <th>Form Name</th>
                          <th>Max Input</th>
                          <th>Place Name</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {!isLoadingTask ? (
                          jobs.length > 0 ? (
                            jobs.map((item) => (
                              <tr key={item.id}>
                                <td>{item.name}</td>
                                <td>{item.description}</td>
                                <td>{item.fee}</td>
                                <td>{item.formName}</td>
                                <td>{item.maxInput}</td>
                                <td>{item.placeName}</td>
                                <td>

                                  <button className="btn btn-outline-warning btn-small-compact border-0"
                                    onClick={() => {
                                      setSelectedItem(item);
                                      setShowModal(true);
                                    }}>
                                    <i className="fa-solid fa-person-circle-plus" style={{ color: 'orange' }}></i>
                                  </button>
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan={8} className="text-center">
                                Tidak ada data
                              </td>
                            </tr>
                          )
                        ) : (
                          <tr>
                            <td colSpan={8} className="text-center">
                              <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Loading...</span>
                              </div>
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>

                    <div className="d-flex justify-content-between align-items-center mt-3">
                      <div className="me-4 d-flex align-items-center">
                        <label className="me-2">Rows per page:</label>
                        <CustomDropdown
                          value={pageSize}
                          options={PageSizeOption.options}
                          onChange={(opt) => {
                            setPageSize(Number(opt.id));
                            setCurrentPage(1);
                          }}
                          align="top"
                        />
                      </div>

                      <div className="d-flex align-items-center">
                        <span className="me-3">
                          {totalRecords > 0
                            ? `${(currentPage - 1) * pageSize + 1}–${Math.min(
                              currentPage * pageSize,
                              totalRecords
                            )} of ${totalRecords}`
                            : '0 data'}
                        </span>

                        <nav>
                          <ul className="pagination mb-0">
                            <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                              <button className="page-link" onClick={() => setCurrentPage(1)}>
                                <i className="fa fa-angle-double-left"></i>
                              </button>
                            </li>
                            <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                              <button
                                className="page-link"
                                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                              >
                                <i className="fa fa-angle-left"></i>
                              </button>
                            </li>

                            <li className="page-item disabled">
                              <span className="page-link">{currentPage}</span>
                            </li>

                            <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                              <button
                                className="page-link"
                                onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                              >
                                <i className="fa fa-angle-right"></i>
                              </button>
                            </li>
                            <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                              <button className="page-link" onClick={() => setCurrentPage(totalPages)}>
                                <i className="fa fa-angle-double-right"></i>
                              </button>
                            </li>
                          </ul>
                        </nav>
                      </div>
                    </div>

                    {showModal && selectedItem && (
                      <ProjectJobDetailModal
                        item={selectedItem}
                        onClose={() => {
                          setShowModal(false)
                          // if (decisionSuccess) {
                          //   fetchData()
                          // }
                        }}
                      />
                    )}
                  </>
                }
                {tabMode === "task" &&
                  <>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <div />
                      <div>
                        <button className="btn btn-outline-primary me-2">Import Plan From CSV</button>
                        <Link to={`/project/assignment/${id}`}>
                          <button className="btn btn-primary">New Task <i className="fa fa-plus ms-1" /></button>
                        </Link>
                      </div>
                    </div>

                    <table className="table table-condensed table-hover">
                      <thead className="table-primary">
                        <tr>
                          <th>Name</th>
                          <th>Store</th>
                          <th>Installer</th>
                          <th>Created By</th>
                          <th>Created At</th>
                          <th>Status</th>
                          <th>Goods</th>
                        </tr>
                      </thead>
                      <tbody>
                        {!isLoadingTask ? (
                          tasks.length > 0 ? (
                            tasks.map((task) => (
                              <tr key={task.id}>
                                <td>{task.name}</td>
                                <td>{task.storeName}</td>
                                <td>{task.installerName}</td>
                                <td>{task.createdBy}</td>
                                <td>{task.createdAt}</td>
                                <td>{task.status}</td>
                                <td>{task.goodsCounts} goods</td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan={8} className="text-center">
                                Tidak ada data
                              </td>
                            </tr>
                          )
                        ) : (
                          <tr>
                            <td colSpan={8} className="text-center">
                              <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Loading...</span>
                              </div>
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>

                    <div className="d-flex justify-content-between align-items-center mt-3">
                      <div className="me-4 d-flex align-items-center">
                        <label className="me-2">Rows per page:</label>
                        <CustomDropdown
                          value={pageSize}
                          options={PageSizeOption.options}
                          onChange={(opt) => {
                            setPageSize(Number(opt.id));
                            setCurrentPage(1);
                          }}
                          align="top"
                        />
                      </div>

                      <div className="d-flex align-items-center">
                        <span className="me-3">
                          {totalRecords > 0
                            ? `${(currentPage - 1) * pageSize + 1}–${Math.min(
                              currentPage * pageSize,
                              totalRecords
                            )} of ${totalRecords}`
                            : '0 data'}
                        </span>

                        <nav>
                          <ul className="pagination mb-0">
                            <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                              <button className="page-link" onClick={() => setCurrentPage(1)}>
                                <i className="fa fa-angle-double-left"></i>
                              </button>
                            </li>
                            <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                              <button
                                className="page-link"
                                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                              >
                                <i className="fa fa-angle-left"></i>
                              </button>
                            </li>

                            <li className="page-item disabled">
                              <span className="page-link">{currentPage}</span>
                            </li>

                            <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                              <button
                                className="page-link"
                                onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                              >
                                <i className="fa fa-angle-right"></i>
                              </button>
                            </li>
                            <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                              <button className="page-link" onClick={() => setCurrentPage(totalPages)}>
                                <i className="fa fa-angle-double-right"></i>
                              </button>
                            </li>
                          </ul>
                        </nav>
                      </div>
                    </div>
                  </>
                }
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProjectJobDetail;